/**
 * Rendering a frozen evidence pack as a document somebody can print, save or hand over.
 *
 * The pack itself is built and frozen in the database (migration 0100). Nothing here computes,
 * filters or summarises: it lays out exactly what was frozen, including the months the satellite
 * could not see and the limitations, because a renderer that quietly dropped a gap would undo the
 * one property the pack exists to have.
 *
 * The hash is printed on the document. An auditor holding a printed pack can ask for the row and
 * compare, which is only useful if the number is on the paper.
 */

export interface PackMonth {
  month: string; status: string; reason: string | null; snapshot_code: string | null;
  observation_date: string | null; ndvi_mean: number | null; change_pct: number | null;
  usable_pixel_fraction: number | null; spatial_confidence: string | null;
  observations_considered: number | null; observations_usable: number | null;
}

export interface PackFinding {
  event_code: string | null; detected_on: string; type: string; severity: string;
  review_status: string; change_pct: number | null; confidence: number | null;
  spatial_confidence: string | null; reason_codes: string[] | null; rule_version: string | null;
  explained_by: string | null; reviewed_by: string | null; reviewed_at: string | null;
  review_remarks: string | null;
}

export interface PackContent {
  organisation?: { name?: string } | null;
  plot?: { plot_code?: string; declared_area_ha?: number; mapped_area_ha?: number; plot_status?: string } | null;
  farmer?: { farmer_code?: string; name?: string } | null;
  village?: { name?: string } | null;
  boundary?: { version_no?: number; area_ha?: number; capture_method?: string; approval_status?: string; approved_at?: string } | null;
  monitoring?: { monitoring_code?: string; status?: string; provider?: string; cadence_days?: number; activated_at?: string } | null;
  baseline?: { acquisition_at?: string; cloud_pct?: number; ndvi_mean?: number; spatial_confidence?: string } | null;
  period?: { from?: string; to?: string } | null;
  months?: PackMonth[];
  findings?: PackFinding[];
  harvest_assessments?: Array<{ declared_harvest_date: string; verdict: string; verdict_reason: string | null; change_pct: number | null; observed_change_on: string | null }>;
  alerts?: Array<{ raised_at: string; severity: string; summary: string | null; notification_status: string; recipients: number; acknowledged_at: string | null; acknowledged_by: string | null }>;
  thresholds_in_force?: Record<string, unknown> | null;
  limitations?: string[];
  built_at?: string;
}

export interface PackRow {
  pack_code: string | null; plot_code: string | null; farmer_name: string | null;
  village_name: string | null; period_from: string; period_to: string; purpose: string;
  content_sha256: string; version_no: number; supersedes_id: string | null;
  supersede_reason: string | null; issued_at: string; issued_by_name: string | null;
  content: PackContent;
}

const MONTH_STATUS: Record<string, string> = {
  no_significant_change: 'No significant change',
  potential_change: 'Potential change — see findings',
  no_usable_observation: 'Not seen',
  not_assessed: 'Not assessed',
};

const PURPOSE: Record<string, string> = {
  fsc_audit: 'FSC audit', eudr_due_diligence: 'EUDR due diligence',
  internal_review: 'Internal review', other: 'Other',
};

function escape(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const cell = (value: unknown, dash = '—') =>
  value === null || value === undefined || value === '' ? dash : escape(value);

const pct = (value: number | null | undefined) =>
  value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`;

/** The pack as a self-contained HTML document: no stylesheet, no script, no external anything. */
export function evidencePackHtml(pack: PackRow): string {
  const c = pack.content ?? {};
  const months = c.months ?? [];
  const findings = c.findings ?? [];
  const harvests = c.harvest_assessments ?? [];
  const alerts = c.alerts ?? [];
  const seen = months.filter((m) => m.status === 'no_significant_change' || m.status === 'potential_change').length;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<title>${escape(pack.pack_code ?? 'Evidence pack')} — ${escape(pack.plot_code)}</title>
<style>
  body { font: 13px/1.5 "Segoe UI", system-ui, sans-serif; color: #1a1a1a; margin: 32px; max-width: 900px; }
  h1 { font-size: 20px; margin: 0 0 2px; }
  h2 { font-size: 14px; margin: 26px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #d5d5d5; }
  .sub { color: #555; margin: 0 0 18px; }
  table { border-collapse: collapse; width: 100%; margin-bottom: 4px; }
  th, td { border: 1px solid #d9d9d9; padding: 5px 7px; text-align: left; vertical-align: top; }
  th { background: #f4f4f4; font-weight: 600; }
  dl { display: grid; grid-template-columns: 180px 1fr; gap: 3px 12px; margin: 0; }
  dt { color: #555; }
  .gap td { background: #fcf7ec; }
  .note { color: #555; font-size: 12px; }
  ul { margin: 6px 0; padding-left: 18px; }
  .hash { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; word-break: break-all; }
  @media print { body { margin: 12mm; } h2 { page-break-after: avoid; } tr { page-break-inside: avoid; } }
</style></head><body>

<h1>Satellite monitoring evidence pack</h1>
<p class="sub">${escape(c.organisation?.name ?? '')} · ${escape(PURPOSE[pack.purpose] ?? pack.purpose)}
  · issued ${escape(new Date(pack.issued_at).toISOString().slice(0, 10))}${pack.issued_by_name ? ` by ${escape(pack.issued_by_name)}` : ''}
  ${pack.version_no > 1 ? `· version ${pack.version_no}` : ''}</p>

<dl>
  <dt>Pack</dt><dd>${cell(pack.pack_code)}</dd>
  <dt>Plot</dt><dd>${cell(pack.plot_code)}${c.plot?.mapped_area_ha ? ` · ${escape(c.plot.mapped_area_ha)} ha mapped` : ''}</dd>
  <dt>Farmer</dt><dd>${cell(pack.farmer_name)}${c.farmer?.farmer_code ? ` (${escape(c.farmer.farmer_code)})` : ''}</dd>
  <dt>Village</dt><dd>${cell(pack.village_name)}</dd>
  <dt>Period</dt><dd>${escape(pack.period_from)} to ${escape(pack.period_to)}</dd>
  <dt>Boundary</dt><dd>${c.boundary
    ? `version ${escape(c.boundary.version_no)} · ${escape(c.boundary.area_ha)} ha · ${escape(c.boundary.capture_method)} · ${escape(c.boundary.approval_status)}`
    : 'No approved boundary'}</dd>
  <dt>Monitoring</dt><dd>${c.monitoring
    ? `${escape(c.monitoring.monitoring_code)} · ${escape(c.monitoring.status)} · every ${escape(c.monitoring.cadence_days)} days · source ${escape(c.monitoring.provider)}`
    : 'This plot is not monitored'}</dd>
  <dt>Baseline</dt><dd>${c.baseline
    ? `${escape(String(c.baseline.acquisition_at).slice(0, 10))} · confidence ${escape(c.baseline.spatial_confidence)}`
    : 'No baseline was established'}</dd>
  <dt>Content hash</dt><dd class="hash">${escape(pack.content_sha256)}</dd>
</dl>
${pack.supersedes_id ? `<p class="note">This version replaces an earlier pack. Reason given: ${escape(pack.supersede_reason)}</p>` : ''}

<h2>Month by month</h2>
<p class="note">${seen} of ${months.length} months have a usable observation. A month marked
  <strong>Not seen</strong> means the plot could not be observed — it does not mean nothing happened.</p>
<table><thead><tr><th>Month</th><th>Result</th><th>NDVI</th><th>Change</th><th>Confidence</th><th>Acquisitions</th></tr></thead><tbody>
${months.map((m) => `<tr${m.status === 'no_usable_observation' || m.status === 'not_assessed' ? ' class="gap"' : ''}>
  <td>${escape(m.month)}</td>
  <td>${escape(MONTH_STATUS[m.status] ?? m.status)}${m.reason ? `<br /><span class="note">${escape(m.reason)}</span>` : ''}</td>
  <td>${m.ndvi_mean === null || m.ndvi_mean === undefined ? '—' : Number(m.ndvi_mean).toFixed(3)}</td>
  <td>${pct(m.change_pct)}</td>
  <td>${cell(m.spatial_confidence)}</td>
  <td>${m.observations_usable ?? 0} usable of ${m.observations_considered ?? 0}</td>
</tr>`).join('\n')}
</tbody></table>

<h2>Findings</h2>
${findings.length === 0 ? '<p class="note">No potential change was raised in this period.</p>' : `
<table><thead><tr><th>Date</th><th>Finding</th><th>Change</th><th>Why it was raised</th><th>Review</th></tr></thead><tbody>
${findings.map((f) => `<tr>
  <td>${escape(f.detected_on)}<br /><span class="note">${cell(f.event_code)}</span></td>
  <td>${escape(f.type)}<br /><span class="note">severity ${escape(f.severity)} · rule ${cell(f.rule_version)}</span></td>
  <td>${pct(f.change_pct)}<br /><span class="note">confidence ${f.confidence === null ? '—' : Number(f.confidence).toFixed(2)} · ${cell(f.spatial_confidence)}</span></td>
  <td>${(f.reason_codes ?? []).map((r) => escape(r)).join(', ') || '—'}${f.explained_by ? `<br /><span class="note">explained by ${escape(f.explained_by)}</span>` : ''}</td>
  <td>${escape(f.review_status)}${f.reviewed_by ? `<br /><span class="note">${escape(f.reviewed_by)}, ${escape(String(f.reviewed_at).slice(0, 10))}</span>` : ''}
      ${f.review_remarks ? `<br /><span class="note">${escape(f.review_remarks)}</span>` : ''}</td>
</tr>`).join('\n')}
</tbody></table>`}

<h2>Declared harvests</h2>
${harvests.length === 0 ? '<p class="note">No harvest was declared in this period.</p>' : `
<table><thead><tr><th>Declared</th><th>What the satellite saw</th><th>Change</th><th>Observed</th></tr></thead><tbody>
${harvests.map((h) => `<tr><td>${escape(h.declared_harvest_date)}</td>
  <td>${escape(h.verdict)}${h.verdict_reason ? `<br /><span class="note">${escape(h.verdict_reason)}</span>` : ''}</td>
  <td>${pct(h.change_pct)}</td><td>${cell(h.observed_change_on)}</td></tr>`).join('\n')}
</tbody></table>`}

<h2>Who was told</h2>
${alerts.length === 0 ? '<p class="note">No alert was raised in this period.</p>' : `
<table><thead><tr><th>Raised</th><th>Alert</th><th>Delivery</th><th>Acknowledged</th></tr></thead><tbody>
${alerts.map((a) => `<tr><td>${escape(String(a.raised_at).slice(0, 10))}</td>
  <td>${cell(a.summary)}<br /><span class="note">severity ${escape(a.severity)}</span></td>
  <td>${escape(a.notification_status)} · ${a.recipients} recipient(s)</td>
  <td>${a.acknowledged_at ? `${escape(a.acknowledged_by ?? 'yes')}, ${escape(String(a.acknowledged_at).slice(0, 10))}` : 'Not acknowledged'}</td>
</tr>`).join('\n')}
</tbody></table>`}

<h2>Rules in force when this pack was issued</h2>
<table><thead><tr><th>Setting</th><th>Value</th></tr></thead><tbody>
${Object.entries(c.thresholds_in_force ?? {}).map(([key, value]) =>
  `<tr><td>${escape(key)}</td><td>${escape(typeof value === 'object' ? JSON.stringify(value) : value)}</td></tr>`).join('\n')
  || '<tr><td colspan="2">No detection profile applied to this plot.</td></tr>'}
</tbody></table>

<h2>What this pack does not tell you</h2>
<ul>${(c.limitations ?? []).map((line) => `<li>${escape(line)}</li>`).join('\n')}</ul>

<p class="note">Frozen at issue. Any change to the underlying records after ${escape(new Date(pack.issued_at).toISOString().slice(0, 16).replace('T', ' '))}
  appears only in a later version of this pack, which names the one it replaces.</p>
</body></html>`;
}
